import cn from 'classnames'
import type { ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import {
  ATTENTION_STATUS_ACTIONS,
  isAttentionSeverityTop,
} from 'partner360/entities/attentionStatuses'

import type { IFinancialCockpitPendingAction } from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { formatCockpitPercent, getCockpitFiscalYearLabel } from '../../../../lib'
import { FULL_YEAR_QUARTER } from '../../../../lib/revenueAndSob/constants'
import { useAttentionDetailSeverity } from '../../hooks/useAttentionDetailSeverity'

import type { ISobQuarterGrid, TSobGridCell } from './getSobQuarterGrid'

import styles from './SobQuarterTable.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.sobActuals
const SEE_DETAILS_KEY = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps.seeDetails

export interface ISobQuarterTableRow {
  category: string
  action: IFinancialCockpitPendingAction
  grid: ISobQuarterGrid
}

interface ISobQuarterTableProps {
  rows: ISobQuarterTableRow[]
  fYear: number
  /** The reader may update SOB; everyone else opens the same dialog to read it. */
  canUpdate: boolean
  onOpen: (action: IFinancialCockpitPendingAction) => void
  isActionBusy: (action: IFinancialCockpitPendingAction) => boolean
  renderBlockedTooltip: (blockedBy?: string, iconClassName?: string) => ReactNode
}

const quarterLabel = (quarter: number, fYear: number) =>
  quarter === FULL_YEAR_QUARTER ? getCockpitFiscalYearLabel(fYear) : `Q${quarter}`

const Missing = () => (
  <span className={styles.missing}>
    <i className={styles.missingDot} aria-hidden="true" />
    <Translation span text={KEYS.missing} />
  </span>
)

const ThisYearCell = ({ cell, className }: { cell: TSobGridCell; className?: string }) => (
  <td className={cn(styles.cell, { [styles.cellMissing]: cell.kind === 'missing' }, className)}>
    {cell.kind === 'missing' ? (
      <Missing />
    ) : cell.kind === 'value' ? (
      formatCockpitPercent(cell.sob)
    ) : (
      ''
    )}
  </td>
)

/**
 * The categories owing SOB actuals, each beside its own SOB of last year and this one, quarter by
 * quarter: the gap reads against the figure it will be compared with. A missing quarter is tinted
 * and named; one not due yet stays empty. Every row opens the SOB dialog on its category — to
 * update it for the PBM, to read it for everyone else — and a category the server keeps closed
 * wears its lock instead.
 */
export const SobQuarterTable = ({
  rows,
  fYear,
  canUpdate,
  onOpen,
  isActionBusy,
  renderBlockedTooltip,
}: ISobQuarterTableProps) => {
  /* Every call of the table wears the status' level, as the steps on the tile do. */
  const severity = useAttentionDetailSeverity()
  const actionColor = severity !== null && isAttentionSeverityTop(severity) ? 'danger' : 'warning'
  const quarters = rows[0]?.grid.quarters ?? []
  const lastYear = getCockpitFiscalYearLabel(fYear - 1)
  const thisYear = getCockpitFiscalYearLabel(fYear)

  return (
    <div className={styles.scroller}>
      <table className={styles.table}>
        <thead>
          <tr className={styles.yearRow}>
            <th />
            <th colSpan={quarters.length} className={styles.yearHead}>
              {lastYear}
            </th>
            <th colSpan={quarters.length} className={cn(styles.yearHead, styles.yearStart)}>
              {thisYear}
            </th>
            <th />
            <th />
          </tr>
          <tr>
            <th className={styles.nameHead}>
              <Translation span text={KEYS.category} />
            </th>
            {quarters.map((quarter) => (
              <th key={`ly-${quarter}`}>{quarterLabel(quarter, fYear - 1)}</th>
            ))}
            {quarters.map((quarter, index) => (
              <th key={`ty-${quarter}`} className={cn({ [styles.yearStart]: index === 0 })}>
                {quarterLabel(quarter, fYear)}
              </th>
            ))}
            <th>
              <Translation span text={KEYS.plan} />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map(({ category, action, grid }) => {
            const isBlocked = Boolean(action.ActionBlockedBy)

            return (
              <tr key={category}>
                <td className={styles.name}>
                  <Translation span text={category || action.ActionTitle} />
                </td>
                {grid.lastYear.map((sob, index) => (
                  <td key={`ly-${grid.quarters[index]}`} className={styles.cell}>
                    {formatCockpitPercent(sob)}
                  </td>
                ))}
                {grid.thisYear.map((cell, index) => (
                  <ThisYearCell
                    key={`ty-${grid.quarters[index]}`}
                    cell={cell}
                    className={cn({ [styles.yearStart]: index === 0 })}
                  />
                ))}
                <td className={styles.cell}>{formatCockpitPercent(grid.plan)}</td>
                <td className={styles.act}>
                  {isBlocked ? (
                    <span className={styles.locked}>
                      {renderBlockedTooltip(action.ActionBlockedBy, styles.lockIcon)}
                      <Button
                        className={styles.button}
                        variant="contained"
                        color={actionColor}
                        size="sm"
                        disabled
                      >
                        <Translation span text={KEYS.locked} />
                      </Button>
                    </span>
                  ) : (
                    <Button
                      className={styles.button}
                      variant="contained"
                      color={actionColor}
                      size="sm"
                      icon="kbase kbase-arrow-right"
                      iconPosition="end"
                      loading={isActionBusy(action)}
                      onClick={() => onOpen(action)}
                    >
                      <Translation
                        span
                        text={
                          canUpdate ? ATTENTION_STATUS_ACTIONS.updateSob.labelKey : SEE_DETAILS_KEY
                        }
                      />
                    </Button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

SobQuarterTable.displayName = 'SobQuarterTable'
